import React, { useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { useNavigate } from 'react-router-dom';

// Fix default Leaflet marker icons (they break with webpack)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// Custom price marker
const priceIcon = (price, hasDiscount) => L.divIcon({
  className: 'property-price-marker',
  html: `<div style="
    background: ${hasDiscount ? '#C05746' : '#1A3622'};
    color: white;
    padding: 6px 12px;
    border-radius: 999px;
    font-weight: 600;
    font-size: 13px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    white-space: nowrap;
    border: 2px solid white;
  ">$${price.toFixed(0)}</div>`,
  iconSize: [60, 30],
  iconAnchor: [30, 15],
});

function FitBounds({ properties }) {
  const map = useMap();
  useEffect(() => {
    const validCoords = properties.filter(p => p.latitude && p.longitude);
    if (validCoords.length === 0) return;
    if (validCoords.length === 1) {
      map.setView([validCoords[0].latitude, validCoords[0].longitude], 12);
    } else {
      const bounds = validCoords.map(p => [p.latitude, p.longitude]);
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [properties, map]);
  return null;
}

const calcDiscounted = (property) => {
  const base = property.discount_rate || 0;
  const maxDur = Math.max(
    property.duration_discounts?.['3months'] || 0,
    property.duration_discounts?.['6months'] || 0,
    property.duration_discounts?.['12months'] || 0
  );
  return property.price * (1 - (base + maxDur + (property.seasonal_discount || 0)) / 100);
};

const totalDiscount = (property) => {
  const base = property.discount_rate || 0;
  const maxDur = Math.max(
    property.duration_discounts?.['3months'] || 0,
    property.duration_discounts?.['6months'] || 0,
    property.duration_discounts?.['12months'] || 0
  );
  return base + maxDur + (property.seasonal_discount || 0);
};

export default function PropertyMap({ properties, height = '600px', singleProperty = false }) {
  const navigate = useNavigate();
  const validProperties = properties.filter(p => p.latitude && p.longitude);

  const defaultCenter = validProperties.length > 0
    ? [validProperties[0].latitude, validProperties[0].longitude]
    : [39.8283, -98.5795]; // US center as fallback

  return (
    <div style={{ height, borderRadius: '12px', overflow: 'hidden' }} data-testid="property-map">
      <MapContainer center={defaultCenter} zoom={12} style={{ height: '100%', width: '100%' }} scrollWheelZoom>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {!singleProperty && <FitBounds properties={validProperties} />}
        {validProperties.map((p) => {
          const discountedPrice = calcDiscounted(p);
          const hasDiscount = totalDiscount(p) > 0;
          return (
            <Marker
              key={p.id}
              position={[p.latitude, p.longitude]}
              icon={priceIcon(discountedPrice, hasDiscount)}
              data-testid={`map-marker-${p.id}`}
            >
              <Popup>
                <div style={{ minWidth: '220px' }}>
                  {p.images && p.images[0] && (
                    <img src={p.images[0]} alt={p.title} style={{ width: '100%', height: '120px', objectFit: 'cover', borderRadius: '8px', marginBottom: '8px' }} />
                  )}
                  <div style={{ fontWeight: 600, fontSize: '15px', marginBottom: '4px' }}>{p.title}</div>
                  <div style={{ color: '#666', fontSize: '13px', marginBottom: '6px' }}>{p.city}</div>
                  <div style={{ fontSize: '13px', color: '#666', marginBottom: '8px' }}>
                    {p.bedrooms} bd • {p.bathrooms} ba • {p.area} sqft
                  </div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '10px' }}>
                    <span style={{ fontSize: '18px', fontWeight: 700, color: '#1A3622' }}>${discountedPrice.toFixed(0)}</span>
                    <span style={{ fontSize: '12px', color: '#666' }}>/month</span>
                    {hasDiscount && (
                      <span style={{ marginLeft: 'auto', background: '#C05746', color: 'white', padding: '2px 8px', borderRadius: '999px', fontSize: '11px', fontWeight: 700 }}>
                        -{totalDiscount(p).toFixed(0)}%
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => navigate(`/property/${p.id}`)}
                    style={{ width: '100%', background: '#1A3622', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '999px', fontWeight: 500, cursor: 'pointer', fontSize: '13px' }}
                    data-testid={`map-view-${p.id}-btn`}
                  >
                    View Details
                  </button>
                </div>
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>
    </div>
  );
}
